import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config";

const DeliveryPricingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [settings, setSettings] = useState({
    base_fee: 25.0,
    base_distance_km: 3.0,
    per_km_charge: 7.0,
    small_order_threshold: 100.0,
    small_order_fee: 10.0,
    small_order_base_fee: 25.0,
    platform_fee: 1.0,
    delivery_gst_percent: 18.0,
    platform_gst_percent: 18.0,
    item_gst_percent: 5.0,
    payment_gateway_fees: 2.0,
    admin_commission: 20.0,
  });

  // Fetch settings from DB
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(
        `${config.apiBaseUrl}/api/settings/delivery-pricing`,
      );
      if (res.data && res.data.success && res.data.settings) {
        setSettings(res.data.settings);
      }
    } catch (err) {
      console.error("Error loading delivery settings:", err);
      setError("Failed to load settings from server. Using default values.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage(null);
      setError(null);

      const res = await axios.put(
        `${config.apiBaseUrl}/api/settings/delivery-pricing`,
        settings,
      );
      if (res.data && res.data.success) {
        setMessage(res.data.message || "Settings updated successfully!");
        if (res.data.settings) {
          setSettings(res.data.settings);
        }
      }
    } catch (err) {
      console.error("Error saving delivery settings:", err);
      setError("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Helper calculation for simulation preview
  const calcSimulatedDelivery = (subtotal, distKm) => {
    const baseDist = settings.base_distance_km || 3.0;
    const totalRoundedKm = Math.ceil(distKm);
    const extraKm = Math.max(0, totalRoundedKm - Math.floor(baseDist));
    const longDistanceFee = extraKm * settings.per_km_charge;
    const baseDelivery = settings.base_fee + longDistanceFee;
    if (subtotal < settings.small_order_threshold) {
      return baseDelivery + settings.small_order_fee;
    }
    return baseDelivery;
  };

  return (
    <div
      className="container"
      style={{ paddingTop: "20px", paddingBottom: "40px" }}
    >
      <div className="page-inner">
        <div className="page-header mb-4">
          <h3 className="fw-bold mb-1" style={{ color: "#FF6D1F" }}>
            <i className="fas fa-calculator me-2"></i> Delivery & Pricing
            Settings
          </h3>
          <p className="text-muted mb-0">
            Centralized control to add, update, and manage delivery fees, base
            charges, distance rates, small order thresholds, and platform fees.
          </p>
        </div>

        {message && (
          <div
            className="alert alert-success alert-dismissible fade show shadow-sm"
            role="alert"
          >
            <i className="fas fa-check-circle me-2"></i> {message}
            <button
              type="button"
              className="btn-close"
              onClick={() => setMessage(null)}
            ></button>
          </div>
        )}

        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show shadow-sm"
            role="alert"
          >
            <i className="fas fa-exclamation-triangle me-2"></i> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">Loading settings...</span>
            </div>
            <p className="mt-2 text-muted fw-semibold">
              Fetching settings from database...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Left Column: Form Fields */}
              <div className="col-lg-8 col-md-12">
                {/* Card 1: Delivery Fee & Distance Settings */}
                <div className="card shadow-sm border-0 mb-4 rounded-3">
                  <div className="card-header bg-white border-bottom py-3">
                    <h5 className="card-title mb-0 text-dark fw-bold">
                      <i className="fas fa-truck text-warning me-2"></i>{" "}
                      Standard Delivery Fee Rules
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label fw-bold text-dark">
                          Base Delivery Fee (₹)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light fw-bold">
                            ₹
                          </span>
                          <input
                            type="number"
                            step="0.5"
                            className="form-control fw-bold"
                            name="base_fee"
                            value={settings.base_fee}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-text">
                          Minimum base fee (e.g. ₹25.00)
                        </div>
                      </div>

                      <div className="col-md-4">
                        <label className="form-label fw-bold text-dark">
                          Base Distance (Covered in Base Fee)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            step="0.5"
                            className="form-control fw-bold"
                            name="base_distance_km"
                            value={settings.base_distance_km}
                            onChange={handleChange}
                            required
                          />
                          <span className="input-group-text bg-light fw-bold">
                            KM
                          </span>
                        </div>
                        <div className="form-text">
                          Covers up to this distance in base fee (controls modal {settings.base_distance_km} km)
                        </div>
                      </div>

                      <div className="col-md-4">
                        <label className="form-label fw-bold text-dark">
                          Per KM Rate (₹ / KM)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light fw-bold">
                            ₹
                          </span>
                          <input
                            type="number"
                            step="0.5"
                            className="form-control fw-bold"
                            name="per_km_charge"
                            value={settings.per_km_charge}
                            onChange={handleChange}
                            required
                          />
                          <span className="input-group-text bg-light">/KM</span>
                        </div>
                        <div className="form-text">
                          Charge per kilometer (e.g. ₹7.00)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2: Small Order Fee Rules */}
                <div className="card shadow-sm border-0 mb-4 rounded-3">
                  <div className="card-header bg-white border-bottom py-3">
                    <h5 className="card-title mb-0 text-dark fw-bold">
                      <i className="fas fa-shopping-basket text-info me-2"></i>{" "}
                      Small Order Threshold & Fees
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-dark">
                          Minimum Subtotal Threshold (₹)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light fw-bold">
                            ₹
                          </span>
                          <input
                            type="number"
                            step="1"
                            className="form-control fw-bold"
                            name="small_order_threshold"
                            value={settings.small_order_threshold}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-text">
                          Orders below this subtotal incur small order fee (e.g.
                          ₹100)
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-bold text-dark">
                          Small Order Surcharge Fee (₹)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light fw-bold">
                            ₹
                          </span>
                          <input
                            type="number"
                            step="1"
                            className="form-control fw-bold"
                            name="small_order_fee"
                            value={settings.small_order_fee}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-text">
                          Extra surcharge added for small orders (e.g. ₹10.00)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 3: Platform Fee & Taxes */}
                <div className="card shadow-sm border-0 mb-4 rounded-3">
                  <div className="card-header bg-white border-bottom py-3">
                    <h5 className="card-title mb-0 text-dark fw-bold">
                      <i className="fas fa-percentage text-success me-2"></i>{" "}
                      Platform Fee & Tax Rates
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label fw-bold text-dark">
                          Platform Fee (₹)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light fw-bold">
                            ₹
                          </span>
                          <input
                            type="number"
                            step="0.5"
                            className="form-control fw-bold"
                            name="platform_fee"
                            value={settings.platform_fee}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-text">
                          Fixed per order (e.g. ₹1.00)
                        </div>
                      </div>

                      <div className="col-md-3">
                        <label className="form-label fw-bold text-dark">
                          Delivery Fee GST (%)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            step="0.5"
                            className="form-control fw-bold"
                            name="delivery_gst_percent"
                            value={settings.delivery_gst_percent}
                            onChange={handleChange}
                            required
                          />
                          <span className="input-group-text bg-light fw-bold">
                            %
                          </span>
                        </div>
                        <div className="form-text">Standard GST (18%)</div>
                      </div>

                      <div className="col-md-3">
                        <label className="form-label fw-bold text-dark">
                          Platform Fee GST (%)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            step="0.5"
                            className="form-control fw-bold"
                            name="platform_gst_percent"
                            value={settings.platform_gst_percent}
                            onChange={handleChange}
                            required
                          />
                          <span className="input-group-text bg-light fw-bold">
                            %
                          </span>
                        </div>
                        <div className="form-text">Standard GST (18%)</div>
                      </div>

                      <div className="col-md-3">
                        <label className="form-label fw-bold text-dark">
                          Item GST Rate (%)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            step="0.5"
                            className="form-control fw-bold"
                            name="item_gst_percent"
                            value={settings.item_gst_percent}
                            onChange={handleChange}
                            required
                          />
                          <span className="input-group-text bg-light fw-bold">
                            %
                          </span>
                        </div>
                        <div className="form-text">Food GST (5%)</div>
                      </div>

                      <div className="col-md-3">
                        <label className="form-label fw-bold text-dark">
                          Admin Commission (%)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            step="0.5"
                            className="form-control fw-bold text-success"
                            name="admin_commission"
                            value={settings.admin_commission}
                            onChange={handleChange}
                            required
                          />
                          <span className="input-group-text bg-light fw-bold">
                            %
                          </span>
                        </div>
                        <div className="form-text">Platform commission (e.g. 20%)</div>
                      </div>

                      <div className="col-md-3">
                        <label className="form-label fw-bold text-dark">
                          Payment Gateway Fee (%)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            step="0.1"
                            className="form-control fw-bold text-danger"
                            name="payment_gateway_fees"
                            value={settings.payment_gateway_fees}
                            onChange={handleChange}
                            required
                          />
                          <span className="input-group-text bg-light fw-bold">
                            %
                          </span>
                        </div>
                        <div className="form-text">Gateway charge (e.g. 2%)</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end mb-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn px-4 py-2.5 fw-bold shadow-sm"
                    style={{
                      backgroundColor: "#FF6D1F",
                      color: "#fff",
                      borderRadius: "8px",
                    }}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving Changes...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i> Save Pricing
                        Settings
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Right Column: Live Simulation & Preview */}
              <div className="col-lg-4 col-md-12">
                <div
                  className="card shadow-sm border-0 rounded-3 mb-4 sticky-top"
                  style={{ top: "90px" }}
                >
                  <div className="card-header bg-dark text-white py-3">
                    <h5 className="card-title mb-0 fw-bold text-warning">
                      <i className="fas fa-eye me-2"></i> Live Calculation
                      Preview
                    </h5>
                  </div>
                  <div className="card-body">
                    <p className="text-muted small">
                      How checkout fees will calculate in customer app based on
                      your active rules:
                    </p>

                    {/* Example 1: Standard Order (2 km, ₹250 subtotal) */}
                    <div className="p-3 bg-light rounded-3 mb-3 border">
                      <div className="d-flex justify-content-between font-bold text-dark small mb-1">
                        <span>Standard Order (2.0 km, Subtotal ₹250)</span>
                      </div>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Delivery Fee:</span>
                        <strong className="text-dark">
                          ₹{calcSimulatedDelivery(250, 2.0).toFixed(2)}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Platform Fee:</span>
                        <strong className="text-dark">
                          ₹{settings.platform_fee.toFixed(2)}
                        </strong>
                      </div>
                    </div>

                    {/* Example 2: Long Distance Order (5.0 km, ₹300 subtotal) */}
                    <div className="p-3 bg-light rounded-3 mb-3 border">
                      <div className="d-flex justify-content-between font-bold text-dark small mb-1">
                        <span>Long Distance (5.0 km, Subtotal ₹300)</span>
                      </div>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>
                          Delivery Fee (Base ₹{settings.base_fee} + {Math.max(0, 5 - (settings.base_distance_km || 3))}km × ₹{settings.per_km_charge}):
                        </span>
                        <strong className="text-dark">
                          ₹{calcSimulatedDelivery(300, 5.0).toFixed(2)}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Platform Fee:</span>
                        <strong className="text-dark">
                          ₹{settings.platform_fee.toFixed(2)}
                        </strong>
                      </div>
                    </div>

                    {/* Example 3: Small Order (< ₹100 subtotal) */}
                    <div className="p-3 bg-warning bg-opacity-10 border border-warning rounded-3 mb-3">
                      <div className="d-flex justify-content-between font-bold text-dark small mb-1">
                        <span>Small Order (1.5 km, Subtotal ₹80)</span>
                      </div>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Base Fee:</span>
                        <strong className="text-dark">
                          ₹{settings.base_fee.toFixed(2)}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Small Order Surcharge:</span>
                        <strong className="text-danger">
                          +₹{settings.small_order_fee.toFixed(2)}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between text-dark font-bold small pt-1 border-top mt-1">
                        <span>Total Delivery Charge:</span>
                        <span className="text-danger">
                          ₹{calcSimulatedDelivery(80, 1.5).toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div
                      className="text-muted text-center"
                      style={{ fontSize: "11px" }}
                    >
                      <i className="fas fa-info-circle me-1 text-primary"></i>
                      All changes save directly to the MySQL database and update
                      customer checkout instantly.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DeliveryPricingSettings;
